import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { formatTrx, formatUsd } from '@/lib/utils';
import {
  Settings,
  RefreshCw,
  Download,
  Share2,
  Info,
  TrendingUp,
  Zap,
  Wifi,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export default function ControlsPanel() {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const {
    startTpd,
    dailyInc,
    days,
    recipientType,
    energyPerTx,
    bwPerTx,
    energyPerTrx,
    bwPerTrx,
    sigCount,
    trxPrice,
    rows,
    set,
    fetchTrxPrice,
    exportCsv,
  } = useAppStore();

  const handleInputChange = (
    field: string,
    value: string | number | undefined
  ) => {
    set({ [field]: value });
  };

  const handleShare = () => {
    const params = new URLSearchParams({
      startTpd: startTpd.toString(),
      dailyInc: dailyInc.toString(),
      days: days.toString(),
      recipientType,
      energyPerTx: energyPerTx.toString(),
      bwPerTx: bwPerTx.toString(),
      energyPerTrx: energyPerTrx.toString(),
      bwPerTrx: bwPerTrx.toString(),
      sigCount: sigCount.toString(),
    });
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center space-x-3 p-3 bg-blue-500/10 rounded-xl border border-blue-500/20 mb-4">
          <Settings className="h-6 w-6 text-blue-400" />
          <h2 className="text-xl font-semibold text-white">
            Transfer Settings
          </h2>
        </div>
        <p className="text-slate-400 text-sm">
          Configure your USDT transfer parameters
        </p>
      </div>

      {/* Recipient Type Toggle - Major Impact Section */}
      <div className="p-6 bg-gradient-to-r from-blue-500/10 to-green-500/10 rounded-xl border border-blue-500/20">
        <h3 className="text-lg font-semibold mb-4 text-white flex items-center space-x-2">
          <Zap className="h-5 w-5 text-blue-400" />
          <span>Recipient Type (Major Cost Impact)</span>
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleInputChange('recipientType', 'existing')}
            className={`p-4 rounded-xl border transition-all ${
              recipientType === 'existing'
                ? 'bg-blue-500/20 border-blue-400 text-blue-200'
                : 'bg-slate-800/50 border-slate-600/50 text-slate-300 hover:bg-slate-700/50'
            }`}
          >
            <div className="text-center">
              <div className="font-semibold mb-2">Existing Recipient</div>
              <div className="text-sm opacity-80">
                ~65,000 Energy per transfer
              </div>
              <div className="text-xs opacity-60 mt-2">
                Address already has USDT
              </div>
            </div>
          </button>

          <button
            onClick={() => handleInputChange('recipientType', 'new')}
            className={`p-4 rounded-xl border transition-all ${
              recipientType === 'new'
                ? 'bg-green-500/20 border-green-400 text-green-200'
                : 'bg-slate-800/50 border-slate-600/50 text-slate-300 hover:bg-slate-700/50'
            }`}
          >
            <div className="text-center">
              <div className="font-semibold mb-2">New Recipient</div>
              <div className="text-sm opacity-80">
                ~130,000 Energy per transfer
              </div>
              <div className="text-xs opacity-60 mt-2">
                Address never received USDT
              </div>
            </div>
          </button>
        </div>

        <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
          <p className="text-sm text-orange-200">
            <strong>⚠️ Cost Impact:</strong> New recipients require 2x more
            energy, significantly increasing your daily costs. Choose carefully
            based on your transfer patterns.
          </p>
        </div>
      </div>

      {/* Quick Staking Summary */}
      {rows.length > 0 && (
        <div className="p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <CheckCircle className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Quick Staking Summary
                </h3>
                <p className="text-purple-300">
                  Exactly what you need to stake for free transactions
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-400">
                100% Free
              </div>
              <div className="text-sm text-purple-300">Daily transactions</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center space-x-2 mb-2">
                <Zap className="h-4 w-4 text-blue-400" />
                <span className="font-medium text-blue-300">
                  Energy Staking
                </span>
              </div>
              <div className="text-white font-semibold text-lg">
                {formatTrx(rows[0]?.trxNeeded || 0)}
              </div>
              <div className="text-slate-400 text-xs leading-tight">
                Daily renewable quota
              </div>
            </div>

            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center space-x-2 mb-2">
                <Wifi className="h-4 w-4 text-green-400" />
                <span className="font-medium text-green-300">
                  Bandwidth Staking
                </span>
              </div>
              <div className="text-white font-semibold text-lg">
                {formatTrx(rows[0]?.trxForBandwidth || 0)}
              </div>
              <div className="text-slate-400 text-xs leading-tight">
                Beyond 600 free bytes/day
              </div>
            </div>

            <div className="p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-4 w-4 text-purple-400" />
                <span className="text-purple-300">Total to Stake</span>
              </div>
              <div className="text-white font-bold text-xl">
                {formatTrx(rows[0]?.totalTrxNeeded || 0)}
              </div>
              <div className="text-xs text-slate-400 leading-tight">
                For completely free transactions
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
            <p className="text-sm text-purple-200 leading-relaxed">
              <strong>🎯 What This Means:</strong> Stake{' '}
              {formatTrx(rows[0]?.totalTrxNeeded || 0)} TRX total to get a daily
              renewable quota that covers both energy and bandwidth needs. Your
              USDT transfers become completely free, saving you{' '}
              {formatUsd((rows[0]?.dailyCostWithoutStaking || 0) * trxPrice)}{' '}
              daily.
            </p>
            <div className="mt-3 p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <p className="text-xs text-blue-200 leading-relaxed">
                <strong>📊 Data Source:</strong> This summary shows the same
                calculations as the main dashboard. The bandwidth staking amount
                reflects the corrected Stake 2.0 formula for accurate TRX
                requirements.
              </p>
            </div>
            <div className="mt-3 p-2 bg-green-500/20 rounded-lg border border-green-500/30">
              <p className="text-xs text-green-200 leading-relaxed">
                <strong>🔄 Auto-Update:</strong> Change any setting above to see
                real-time updates. The calculations automatically use the
                corrected Stake 2.0 formula for bandwidth.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Settings */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h3 className="text-lg font-semibold mb-6 text-white flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-blue-400" />
          <span>Transaction Settings</span>
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Starting Transfers per Day
            </label>
            <input
              type="number"
              value={startTpd}
              onChange={(e) =>
                handleInputChange('startTpd', parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="e.g., 100"
            />
            <p className="text-sm text-slate-400 mt-2">
              How many USDT transfers you make per day initially
            </p>
          </div>

          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Daily Growth Rate
            </label>
            <input
              type="number"
              value={dailyInc}
              onChange={(e) =>
                handleInputChange('dailyInc', parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="e.g., 10"
            />
            <p className="text-sm text-slate-400 mt-2">
              Additional transfers added each day (0 = no growth)
            </p>
          </div>

          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Projection Period (Days)
            </label>
            <input
              type="number"
              value={days}
              onChange={(e) =>
                handleInputChange('days', parseInt(e.target.value) || 30)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="e.g., 30"
            />
            <p className="text-sm text-slate-400 mt-2">
              How many days to project into the future
            </p>
          </div>
        </div>
      </div>

      {/* Resource Configuration */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h3 className="text-lg font-semibold mb-6 text-white flex items-center space-x-2">
          <Zap className="h-5 w-5 text-yellow-400" />
          <span>Resource Configuration</span>
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Energy per Transaction
            </label>
            <input
              type="number"
              value={energyPerTx}
              onChange={(e) =>
                handleInputChange('energyPerTx', parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="e.g., 65000"
            />
            <p className="text-sm text-slate-400 mt-2">
              Energy units consumed per USDT transfer
            </p>
          </div>

          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Bandwidth per Transaction
            </label>
            <input
              type="number"
              value={bwPerTx}
              onChange={(e) =>
                handleInputChange('bwPerTx', parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="e.g., 350"
            />
            <p className="text-sm text-slate-400 mt-2">
              Bandwidth bytes consumed per transfer
            </p>
            <div className="mt-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-sm text-green-200">
                <strong>💡 Free Bandwidth:</strong> TRON provides 600 free
                bandwidth bytes per day. Only usage beyond this limit incurs
                charges.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Settings - Progressive Disclosure */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <button
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="flex items-center justify-between w-full text-lg font-semibold text-white hover:text-blue-400 transition-colors"
        >
          <span className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-purple-400" />
            <span>Advanced Settings</span>
          </span>
          {isAdvancedOpen ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </button>

        {isAdvancedOpen && (
          <div className="mt-6 space-y-6 pt-6 border-t border-slate-700/50">
            <div>
              <label className="block text-base font-medium text-slate-300 mb-3">
                Energy per TRX (staking yield)
              </label>
              <input
                type="number"
                value={energyPerTrx}
                onChange={(e) =>
                  handleInputChange(
                    'energyPerTrx',
                    parseInt(e.target.value) || 0
                  )
                }
                className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="e.g., 1000"
              />
              <p className="text-sm text-slate-400 mt-2">
                Daily Energy quota you get per TRX staked
              </p>
            </div>

            <div>
              <label className="block text-base font-medium text-slate-300 mb-3">
                Bandwidth per TRX (staking yield)
              </label>
              <input
                type="number"
                value={bwPerTrx}
                onChange={(e) =>
                  handleInputChange('bwPerTrx', parseInt(e.target.value) || 0)
                }
                className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="e.g., 1000"
              />
              <p className="text-sm text-slate-400 mt-2">
                Daily Bandwidth quota you get per TRX staked
              </p>
            </div>

            <div>
              <label className="block text-base font-medium text-slate-300 mb-3">
                Signature Count
              </label>
              <input
                type="number"
                value={sigCount}
                onChange={(e) =>
                  handleInputChange('sigCount', parseInt(e.target.value) || 1)
                }
                className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="e.g., 1"
              />
              <p className="text-sm text-slate-400 mt-2">
                Number of signatures required (usually 1)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* TRX Price Status */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Live TRX Price</h3>
          <button
            onClick={fetchTrxPrice}
            className="p-3 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
            title="Refresh TRX price"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <span className="text-lg text-green-200">
            Current Price: ${trxPrice?.toFixed(4) || '0.0000'}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <button
          onClick={exportCsv}
          className="w-full flex items-center justify-center space-x-3 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors text-lg"
        >
          <Download className="h-5 w-5" />
          <span>Export to CSV</span>
        </button>

        <button
          onClick={handleShare}
          className="w-full flex items-center justify-center space-x-3 p-4 bg-slate-600 hover:bg-slate-700 text-white rounded-xl font-medium transition-colors text-lg"
        >
          <Share2 className="h-5 w-5" />
          <span>Share Configuration</span>
        </button>
      </div>

      {/* Help Section */}
      <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/20">
        <h4 className="font-medium mb-4 text-blue-200 flex items-center space-x-2">
          <Info className="h-5 w-5" />
          <span>How to Use</span>
        </h4>
        <div className="text-sm text-blue-100 space-y-2">
          <p>1. Set your daily transfer volume and growth rate</p>
          <p>2. Choose recipient type (new vs existing)</p>
          <p>3. Configure your staking yields</p>
          <p>4. See optimal TRX to stake and daily savings</p>
        </div>
      </div>
    </div>
  );
}
